import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { API, apiError, formatINR } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Plus, Edit2, Trash2, X, User, Phone, BookOpen, Sparkles } from "lucide-react";
import { toast } from "sonner";
import BulkSelectBar from "../components/BulkSelectBar";

const EMPTY = { name: "", phone: "", email: "", address: "", opening_balance: 0, notes: "" };

export default function Customers() {
  const { user } = useAuth();
  const canBulkDelete = user?.role === "admin" || user?.role === "branch_head";
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await API.get("/customers"); setItems(data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setModal("new"); };
  const openEdit = (c) => {
    setForm({
      name: c.name, phone: c.phone || "", email: c.email || "",
      address: c.address || "", opening_balance: c.opening_balance || 0, notes: c.notes || "",
    });
    setModal(c);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, opening_balance: parseFloat(form.opening_balance) || 0 };
      if (modal === "new") await API.post("/customers", payload);
      else await API.put(`/customers/${modal.id}`, payload);
      toast.success("Saved"); setModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete customer ${c.name}?`)) return;
    try {
      await API.delete(`/customers/${c.id}`);
      toast.success("Deleted"); load();
    } catch (err) {
      const status = err?.response?.status;
      const detail = apiError(err);
      if (status === 409) {
        if (window.confirm(`${detail}\n\nForce delete ${c.name} and all ledger entries?`)) {
          try {
            await API.delete(`/customers/${c.id}?force=true`);
            toast.success("Deleted with history"); load();
          } catch (e2) { toast.error(apiError(e2)); }
        }
      } else { toast.error(detail); }
    }
  };

  const toggleOne = (id) => setSelected((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const bulkDelete = async () => {
    if (!window.confirm(`Delete ${selected.size} selected customers? This cascades — all ledger entries for those customers will be removed. Cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const { data } = await API.post("/bulk-delete", { entity: "customers", ids: [...selected], force: true });
      toast.success(`${data.deleted} deleted${data.skipped.length ? ` · ${data.skipped.length} skipped` : ""}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBulkBusy(false); }
  };

  const visible = items.filter((c) => filter === "all" ? true : c.balance > 0);

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Buyers</div>
          <h1 className="font-display text-3xl font-bold">Customers</h1>
          <p className="text-sm text-slate-500 mt-1">Track retail customers and their credit balances. New customers are auto-created on credit sales.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="add-customer-btn"><Plus className="w-4 h-4" /> New Customer</button>
      </div>

      {canBulkDelete && (
        <BulkSelectBar
          count={selected.size}
          total={visible.length}
          onClear={() => setSelected(new Set())}
          onSelectAll={() => setSelected(new Set(visible.map((c) => c.id)))}
          onDelete={bulkDelete}
          busy={bulkBusy}
          entityLabel="customers"
        />
      )}

      <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 w-fit">
        {[["all", "All"], ["due", "With Balance Due"]].map(([v, l]) => (
          <button key={v} onClick={() => setFilter(v)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${filter === v ? "bg-[#002FA7] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            {l}
          </button>
        ))}
      </div>

      {loading ? <div className="text-slate-500 text-sm">Loading…</div> : visible.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <User className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <div className="font-display text-lg mb-1">No customers {filter === "due" ? "with outstanding balance" : "yet"}</div>
          <div className="text-sm text-slate-500 mb-4">Customers are auto-created when you record a credit sale, or add them manually here.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {visible.map((c) => (
            <div key={c.id} className={`bg-white border rounded-xl p-5 flex flex-col ${selected.has(c.id) ? "border-[#002FA7] ring-2 ring-[#002FA7]/15" : "border-slate-200"}`} data-testid={`customer-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  {canBulkDelete && (
                    <input type="checkbox" data-testid={`customer-select-${c.id}`}
                      checked={selected.has(c.id)} onChange={() => toggleOne(c.id)}
                      className="w-4 h-4" />
                  )}
                  <div className="w-10 h-10 rounded-lg bg-[#002FA7]/5 text-[#002FA7] flex items-center justify-center">
                    <User className="w-5 h-5" />
                  </div>
                </div>
                <div className="flex gap-1">
                  <Link to={`/customer/${c.id}`} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#002FA7]" title="Open ledger" data-testid={`open-cust-ledger-${c.id}`}>
                    <BookOpen className="w-3.5 h-3.5" />
                  </Link>
                  <button onClick={() => openEdit(c)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#002FA7]"><Edit2 className="w-3.5 h-3.5" /></button>
                  <button onClick={() => del(c)} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              <h3 className="font-display text-lg font-semibold mt-3 flex items-center gap-2">
                {c.name}
                {c.auto_created && <Sparkles className="w-3.5 h-3.5 text-amber-500" title="Auto-created from credit sale" />}
              </h3>
              <div className="mt-1 space-y-0.5 text-xs text-slate-500">
                {c.phone && <div className="flex items-center gap-1.5"><Phone className="w-3 h-3" /> {c.phone}</div>}
                {c.email && <div>{c.email}</div>}
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex justify-between items-center">
                <span className="overline text-[10px]">Balance</span>
                <span className={`font-display font-bold text-lg ${c.balance > 0 ? "text-red-600" : c.balance < 0 ? "text-emerald-600" : "text-slate-400"}`}>
                  {formatINR(Math.abs(c.balance || 0))}
                  {c.balance > 0 && <span className="text-xs font-normal text-slate-500"> due</span>}
                  {c.balance < 0 && <span className="text-xs font-normal text-slate-500"> advance</span>}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-xl shadow-2xl w-full max-w-xl">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display text-xl font-semibold">{modal === "new" ? "New Customer" : "Edit Customer"}</h3>
              <button type="button" onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3">
              <L label="Name *" full><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" /></L>
              <L label="Phone *"><input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="inp" /></L>
              <L label="Email"><input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp" /></L>
              <L label="Address" full><textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="inp resize-none" /></L>
              <L label="Opening Balance (₹)" full>
                <input type="number" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} className="inp" />
                <div className="text-xs text-slate-500 mt-1">Positive = customer owes you. Negative = advance paid.</div>
              </L>
              <L label="Notes" full><input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="inp" /></L>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-customer-btn">Save</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #E2E8F0;border-radius:8px;padding:.5rem .75rem;font-size:.875rem;outline:none;font-family:inherit}.inp:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>
    </div>
  );
}
function L({ label, children, full }) { return <label className={`block ${full ? "md:col-span-2" : ""}`}><div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>{children}</label>; }
